import { FloatingTree } from '@floating-ui/react'
import { hasBlockInteraction, useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { IconBlock } from '@lighthouse/block/src/blocks'
import { EVENT_TYPE, type FloatBlockAbstract, type IconBlockAbstract } from '@lighthouse/core'
import {
    flexMinSizeStyle,
    getWithScopeIdAndRecordIdAndColumnId,
    MultipleFloatBoxProvider,
    useActionRunningLoadings
} from '@lighthouse/shared'
import { reduce } from 'rambda'
import React, { useCallback, useMemo } from 'react'

import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useFloatBlock } from '@/hooks/useFloatBlock'

interface IconBlockProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: IconBlockAbstract
}
const IconBlockController: React.FC<IconBlockProps> = ({ blockData, children, style, ...rest }) => {
    const {
        id: blockId,
        config: { floatPointerList, eventList }
    } = blockData
    const { handleActionListTrigger } = useActionTrigger(blockId)
    const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(blockId, scope, record?.id), [blockId, scope, record?.id])
    const floatBlockList = useMemo(() => {
        return reduce<string, FloatBlockAbstract[]>(
            (pre, id) => {
                const block = blockData.children.find(v => v.id === id)
                return block ? [...pre, block as FloatBlockAbstract] : pre
            },
            [],
            floatPointerList ?? []
        )
    }, [blockData.children, floatPointerList])
    const provideValue = useFloatBlock(floatBlockList, scope)
    const clickEvent = useMemo(() => eventList?.find(ev => ev.type === EVENT_TYPE.CLICK), [eventList])
    const hasClickEvent = !!clickEvent
    const hasInteraction = useMemo(() => hasBlockInteraction({ hasAction: hasClickEvent, floatBlockList }), [floatBlockList, hasClickEvent])

    const handleClickEvent = useCallback(() => {
        if (!clickEvent || loadings[blockId]) {
            return
        }
        handleActionTriggerWithLoading({
            id: uniqueId,
            actorEvent: clickEvent,
            trigger: (id, actorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record })
        })
    }, [blockId, clickEvent, handleActionListTrigger, handleActionTriggerWithLoading, loadings, record, uniqueId])

    return (
        <FloatingTree>
            <MultipleFloatBoxProvider {...provideValue}>
                <IconBlock
                    data-stop-action-propagation={hasInteraction ? true : undefined}
                    onClickEvent={handleClickEvent}
                    blockData={blockData}
                    isFloatChildren={!!children}
                    style={{ ...flexMinSizeStyle, ...style, cursor: hasInteraction ? 'pointer' : undefined }}
                    {...rest}
                />
                {children}
            </MultipleFloatBoxProvider>
        </FloatingTree>
    )
}

export default IconBlockController
